import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { BoundTypeEnum } from "src/features/route-planning/constants"
import { RoutePlanningModel } from "src/features/route-planning/models"
import {
  DrawResult,
  isCircle,
  isPolygon,
  isRectangle,
  Overlay,
  OverlayGeometry,
  Snapshot,
} from "src/features/route-planning/models/RouteMap.model"
import { appConstants } from "src/features/shared-module/constants"
import {
  getDataFromLocalStorage,
  setDataInLocalStorage,
} from "src/features/shared-module/functions/common.functions"
import { RootState } from "../apis"

const initialState: {
  routeList: RoutePlanningModel[]
  postalCodeStyles: Record<string, PostalCodeStyle>
  drawingDetail: {
    past: Array<Array<Overlay>>
    now: Array<Overlay>
    future: Array<Array<Overlay>>
  }
  editMode: {
    routeId: number
    editView: boolean
    boundType: BoundTypeEnum
  } | null
} = {
  routeList: getDataFromLocalStorage(appConstants.tmpRoutes) || [],
  postalCodeStyles: {},
  drawingDetail: {
    past: [],
    now: [],
    future: [],
  },
  editMode: null,
}

export const routePlanningSlice = createSlice({
  name: "routePlanning",
  initialState,
  reducers: {
    updateDrawingDetail: (state) => {
      const overlays = state.drawingDetail.now.map((overlay: Overlay) => {
        const { geometry } = overlay
        const snapshot: Snapshot = getSnapShotDetail(geometry)
        return {
          ...overlay,
          snapshot,
        }
      })

      state.drawingDetail = {
        now: [...overlays],
        past: [...state.drawingDetail.past, state.drawingDetail.now],
        future: [],
      }
    },
    createDrawingDetail: (
      state,
      action: PayloadAction<{
        drawResult: DrawResult
      }>,
    ) => {
      const { overlay, type } = action.payload.drawResult

      const snapshot: Snapshot = getSnapShotDetail(overlay)

      state.drawingDetail = {
        past: [...state.drawingDetail.past, state.drawingDetail.now],
        now: [
          ...state.drawingDetail.now,
          {
            type: type,
            geometry: overlay,
            snapshot,
          },
        ],
        future: [],
      }
    },
    undoDrawingDetail: (state, action: PayloadAction<number>) => {},
    redoDrawingDetail: (state, action: PayloadAction<number>) => {},

    setEditMode: (
      state,
      action: PayloadAction<{
        routeId: number
        editView: boolean
        boundType: BoundTypeEnum
      } | null>,
    ) => {
      state.editMode = action.payload
    },
    setRouteList: (state, action: PayloadAction<RoutePlanningModel[]>) => {
      state.routeList = action.payload
    },
    createOrUpdateRoute: (state, action: PayloadAction<RoutePlanningModel>) => {
      const newRoute = action.payload
      if (newRoute.id) {
        const index = state.routeList.findIndex((e) => e.id === newRoute.id)
        if (index > -1) {
          state.routeList[index] = newRoute
        }
      } else {
        newRoute.id = new Date().getTime()
        const newList = [action.payload, ...state.routeList]
        setDataInLocalStorage(appConstants.tmpRoutes, newList)
        state.routeList = newList
      }
    },
    deleteRoute: (state, action: PayloadAction<number>) => {
      const routeId = action.payload
      state.routeList = state.routeList.filter((e) => e.id !== routeId)
    },
    addPostalCodeStyle(state, action: PayloadAction<PostalCodeStyle>) {
      const { postalCode } = action.payload
      state.postalCodeStyles[postalCode] = action.payload
    },
  },
})

export const {
  setRouteList,
  createOrUpdateRoute,
  deleteRoute,
  addPostalCodeStyle,
  updateDrawingDetail,
  createDrawingDetail,
  undoDrawingDetail,
  redoDrawingDetail,
  setEditMode,
} = routePlanningSlice.actions

export const routePlanningState = (state: RootState) => state.routePlanning

export const drawingModeSelector = createSelector(
  routePlanningState,
  (state) => state.editMode?.boundType === BoundTypeEnum.user_drawn_area,
)

type PostalCodeStyle = {
  postalCode: string
  placeId: string
  style: google.maps.FeatureStyleOptions
  location: any
}

const getSnapShotDetail = (oGeometry: OverlayGeometry): Snapshot => {
  const snapshot: Snapshot = {}
  if (isCircle(oGeometry)) {
    snapshot.center = oGeometry.getCenter()?.toJSON()
    snapshot.radius = oGeometry.getRadius()
  } else if (isPolygon(oGeometry)) {
    snapshot.path = oGeometry.getPath()?.getArray()
  } else if (isRectangle(oGeometry)) {
    snapshot.bounds = oGeometry.getBounds()?.toJSON()
  }

  return snapshot
}

export const getSnapshotValue = (oGeometry: OverlayGeometry): any => {
  let snapshot: any = {}
  if (isCircle(oGeometry)) {
    snapshot.center = oGeometry.getCenter()?.toJSON()
    snapshot.radius = oGeometry.getRadius()
  } else if (isPolygon(oGeometry)) {
    snapshot = oGeometry
      .getPath()
      ?.getArray()
      .map((e) => {
        return {
          lat: e.lat(),
          lng: e.lng(),
        }
      })
  } else if (isRectangle(oGeometry)) {
    snapshot = oGeometry.getBounds()?.toJSON()
  }
  return snapshot
}
